import { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader, UserPlus, X, Trash2, CheckCircle2, Search, ChevronDown, AlertCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import {
  ASSIGNMENT_ROLES,
  type AssignmentRole,
  type AssignmentStatus,
  type BuildAssignment,
  type BuildTeamMember,
} from '../../types/buildTeam';
import { buildTeamApiService } from '../../services/buildTeamApi';
import { usernameCacheService } from '../../services/usernameCache';
import { useDebounce } from '../../hooks/useDebounce';
import { useStaffListSearch } from '../../hooks/useStaffListSearch';
import { CreatorProfile } from '../CreatorProfile';
import { PlayerProfile } from '../PlayerProfile';
import { ConfirmationModal } from '../../components/ConfirmationModal';
import { SectionHeader } from '../ui/SectionHeader';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonLoader } from '../SkeletonLoader';
import { cn } from '../../utils/cn';

export type BuildAssignmentsTargetType = 'project' | 'build';

export interface BuildAssignmentsSectionProps {
  targetId: string;
  targetType: BuildAssignmentsTargetType;
  canLead: boolean;
  onUpdate: () => void;
  /** Bump this to open the assignment form, e.g. from an Assign builder button elsewhere on the page. */
  openAddFormSignal?: number;
}

function roleBadgeClass(role: AssignmentRole): string {
  switch (role) {
    case 'builder':
      return 'badge-info';
    case 'lead':
      return 'badge-purple';
    case 'reviewer':
      return 'badge-amber';
    default:
      return 'badge-neutral';
  }
}

function statusBadgeClass(status: AssignmentStatus): string {
  switch (status) {
    case 'active':
      return 'badge-success';
    case 'completed':
      return 'badge-info';
    case 'removed':
      return 'badge-danger';
    default:
      return 'badge-neutral';
  }
}

async function fetchAssignments(
  targetType: BuildAssignmentsTargetType,
  targetId: string
): Promise<BuildAssignment[]> {
  if (targetType === 'project') {
    return buildTeamApiService.getProjectAssignments(targetId);
  }
  return buildTeamApiService.getBuildAssignments(targetId);
}

const LABEL_CLASS = 'text-xs font-medium text-glass-400 mb-1.5 block';

export function BuildAssignmentsSection({
  targetId,
  targetType,
  canLead,
  onUpdate,
  openAddFormSignal = 0,
}: BuildAssignmentsSectionProps) {
  const [assignments, setAssignments] = useState<BuildAssignment[]>([]);
  const [membersById, setMembersById] = useState<Record<string, BuildTeamMember | null>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const {
    matches: staffMatches,
    searching,
    error: staffListError,
  } = useStaffListSearch({
    debouncedQuery: debouncedSearchQuery,
    enabled: showAddForm,
  });
  const [memberResolveError, setMemberResolveError] = useState<string | null>(null);
  const [resolvingSelection, setResolvingSelection] = useState(false);
  const [selectedMember, setSelectedMember] = useState<BuildTeamMember | null>(null);
  const [newRole, setNewRole] = useState<AssignmentRole>('builder');
  const [submittingAssignment, setSubmittingAssignment] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ type: 'remove' | 'complete'; id: string } | null>(null);

  const loadAssignments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchAssignments(targetType, targetId);
      setAssignments(list);
      const memberIds = [...new Set(list.map((a) => a.member_id))];
      const assignerIds = [...new Set(list.map((a) => a.assigned_by).filter(Boolean))];
      if (assignerIds.length > 0) {
        usernameCacheService.prefetchUsernames(assignerIds);
      }
      const entries = await Promise.all(
        memberIds.map(async (id) => {
          try {
            const m = await buildTeamApiService.getTeamMemberById(id);
            return [id, m] as const;
          } catch {
            return [id, null] as const;
          }
        })
      );
      setMembersById(Object.fromEntries(entries));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  }, [targetId, targetType]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  useEffect(() => {
    if (openAddFormSignal > 0 && canLead) setShowAddForm(true);
  }, [openAddFormSignal, canLead]);

  const formatAssignedAt = (ts: number) => {
    const date = ts > 1e12 ? new Date(ts) : new Date(ts * 1000);
    return formatDistanceToNow(date, { addSuffix: true });
  };

  const handleAddAssignment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMember || submittingAssignment) return;
    setSubmittingAssignment(true);
    try {
      const body = { member_id: selectedMember.id, role: newRole };
      if (targetType === 'project') {
        await buildTeamApiService.assignToProject(targetId, body);
      } else {
        await buildTeamApiService.assignToBuild(targetId, body);
      }
      setShowAddForm(false);
      setSearchQuery('');
      setMemberResolveError(null);
      setSelectedMember(null);
      setNewRole('builder');
      await loadAssignments();
      onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add assignment');
    } finally {
      setSubmittingAssignment(false);
    }
  };

  const handleRemove = async (assignmentId: string) => {
    if (!canLead || removingId) return;
    setConfirmAction({ type: 'remove', id: assignmentId });
  };

  const handleUpdateRole = async (assignmentId: string, role: AssignmentRole) => {
    if (updatingId) return;
    setUpdatingId(assignmentId);
    try {
      await buildTeamApiService.updateAssignment(assignmentId, { role });
      await loadAssignments();
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleComplete = async (assignmentId: string) => {
    if (updatingId) return;
    setConfirmAction({ type: 'complete', id: assignmentId });
  };

  const executeConfirmAction = async () => {
    if (!confirmAction) return;
    if (confirmAction.type === 'remove') {
      setRemovingId(confirmAction.id);
      try {
        await buildTeamApiService.removeAssignment(confirmAction.id);
        await loadAssignments();
        onUpdate();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to remove assignment');
      } finally {
        setRemovingId(null);
        setConfirmAction(null);
      }
    } else if (confirmAction.type === 'complete') {
      setUpdatingId(confirmAction.id);
      try {
        await buildTeamApiService.completeAssignment(confirmAction.id);
        await loadAssignments();
        onUpdate();
      } catch (err) {
        alert(err instanceof Error ? err.message : 'Failed to complete assignment');
      } finally {
        setUpdatingId(null);
        setConfirmAction(null);
      }
    }
  };

  const closeAddForm = () => {
    setShowAddForm(false);
    setSearchQuery('');
    setMemberResolveError(null);
    setSelectedMember(null);
    setNewRole('builder');
  };

  const sortedAssignments = useMemo(
    () => [...assignments].sort((a, b) => a.assigned_at - b.assigned_at),
    [assignments]
  );

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <SkeletonLoader className="h-4 w-32" />
          <SkeletonLoader className="h-9 w-36" />
        </div>
        <div className="divide-y divide-glass-200/10">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="py-3 first:pt-0 space-y-2">
              <div className="flex items-center gap-2">
                <SkeletonLoader className="h-4 w-32" />
                <SkeletonLoader className="h-5 w-16 rounded-full" />
                <SkeletonLoader className="h-5 w-14 rounded-full" />
              </div>
              <SkeletonLoader className="h-3 w-40" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        bare
        compact
        tone="danger"
        icon={AlertCircle}
        title="Failed to load assignments"
        description={error}
        action={
          <button type="button" onClick={loadAssignments} className="glass-button">
            Retry
          </button>
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Assignments"
        icon={UserPlus}
        count={assignments.length}
        className="mb-0"
        description={canLead ? undefined : 'Only build leads and admins can assign or remove builders. Ask a lead if someone needs adding here.'}
        actions={
          canLead && !showAddForm ? (
            <button
              type="button"
              data-tour="build-assign"
              onClick={() => setShowAddForm(true)}
              className="glass-button"
            >
              <UserPlus className="w-4 h-4" />
              Assign builder
            </button>
          ) : undefined
        }
      />

      {canLead && showAddForm && (
        <form
          onSubmit={handleAddAssignment}
          className="subcard p-4 space-y-3"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-white">Assign a builder to this {targetType}</span>
            <button
              type="button"
              onClick={closeAddForm}
              className="btn-icon btn-icon-sm"
              aria-label="Close"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-glass-400">
            Search staff by username and pick a role. They need to be on the build team roster first; a build admin adds them under{' '}
            <Link to="/build-team?tab=team" className="text-accent-300 hover:underline underline-offset-2">Build Team → Team</Link>.
          </p>
          <div>
            <label className={LABEL_CLASS}>Team member</label>
            {selectedMember ? (
              <div className="flex items-center justify-between gap-2 glass-input px-3 py-2 text-sm">
                <span className="text-white">{selectedMember.display_name}</span>
                <button
                  type="button"
                  onClick={() => setSelectedMember(null)}
                  className="text-xs text-accent-300 hover:text-accent-200 hover:underline underline-offset-2"
                >
                  Change
                </button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
                  <input
                    type="search"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setMemberResolveError(null);
                    }}
                    placeholder="Search staff by username…"
                    className="glass-input w-full pl-9"
                    autoComplete="off"
                  />
                  {searching && (
                    <Loader className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-glass-400" />
                  )}
                </div>
                {staffListError && (
                  <p className="text-xs text-red-400 mt-1">{staffListError}</p>
                )}
                {searchQuery.length > 0 && searchQuery.length < 2 && (
                  <p className="text-xs text-glass-500 mt-1">Type at least 2 characters…</p>
                )}
                {memberResolveError && (
                  <p className="text-xs text-red-400 mt-1">{memberResolveError}</p>
                )}
                {debouncedSearchQuery.length >= 2 &&
                  !searching &&
                  !staffListError &&
                  staffMatches.length === 0 && (
                    <p className="text-xs text-glass-500 mt-1">No staff found.</p>
                  )}
                {staffMatches.length > 0 && (
                  <div className="mt-1 rounded-lg border border-glass-200/10 overflow-hidden max-h-[240px] overflow-y-auto bg-dark-900/60 divide-y divide-glass-200/10">
                    {staffMatches.map((s) => {
                      const uuid = s.minecraft_uuid;
                      const displayName =
                        usernameCacheService.getName(uuid) ?? uuid;
                      return (
                        <button
                          key={uuid}
                          type="button"
                          disabled={resolvingSelection}
                          onClick={async () => {
                            setMemberResolveError(null);
                            setResolvingSelection(true);
                            try {
                              const m = await buildTeamApiService.getTeamMemberByUuid(uuid);
                              setSelectedMember(m);
                              setSearchQuery('');
                            } catch {
                              setMemberResolveError(
                                'That staff member is not on the build team roster yet. A build admin can add them under Build Team → Team, then they can be assigned.'
                              );
                            } finally {
                              setResolvingSelection(false);
                            }
                          }}
                          className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-glass-200/5 transition-colors text-sm disabled:opacity-50"
                        >
                          <PlayerProfile uuid={uuid} name={displayName} inline showUuid={false} />
                          <span className="text-[10px] text-glass-500 font-mono flex-shrink-0">
                            {uuid.slice(0, 8)}...
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
          <div>
            <label htmlFor="assignment-role" className={LABEL_CLASS}>
              Role
            </label>
            <select
              id="assignment-role"
              value={newRole}
              onChange={(e) => setNewRole(e.target.value as AssignmentRole)}
              className="glass-select w-full"
            >
              {ASSIGNMENT_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r.charAt(0).toUpperCase() + r.slice(1)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={!selectedMember || submittingAssignment}
              className="btn-primary"
            >
              {submittingAssignment ? <Loader className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              Assign
            </button>
          </div>
        </form>
      )}

      <div>
        {sortedAssignments.length === 0 ? (
          <EmptyState
            bare
            compact
            icon={UserPlus}
            title="Nobody assigned yet"
            description={canLead ? 'Assign a builder from the team roster and choose their role.' : 'Builders assigned by a build lead will appear here.'}
            action={canLead && !showAddForm ? <button type="button" className="btn-primary" onClick={() => setShowAddForm(true)}><UserPlus className="w-4 h-4" />Assign the first builder</button> : undefined}
          />
        ) : (
          <ul className="divide-y divide-glass-200/10">
            {sortedAssignments.map((a) => {
              const member = membersById[a.member_id];
              const displayName = member?.display_name ?? 'Unknown member';
              const isUpdating = updatingId === a.id;
              return (
                <li
                  key={a.id}
                  className="flex items-start gap-3 py-3 first:pt-0 last:pb-0"
                >
                  <div className="flex-1 min-w-0 space-y-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-white">{displayName}</span>
                      {canLead && a.status === 'active' ? (
                        <span className="relative inline-flex">
                          <select
                            value={a.role}
                            onChange={(e) => handleUpdateRole(a.id, e.target.value as AssignmentRole)}
                            disabled={isUpdating}
                            className={cn(
                              'badge appearance-none pr-5 capitalize cursor-pointer',
                              roleBadgeClass(a.role),
                              isUpdating && 'opacity-50'
                            )}
                            title="Change role"
                            aria-label="Assignment role"
                          >
                            {ASSIGNMENT_ROLES.map((r) => (
                              <option key={r} value={r}>
                                {r.charAt(0).toUpperCase() + r.slice(1)}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 pointer-events-none opacity-70" />
                        </span>
                      ) : (
                        <span className={cn('badge capitalize', roleBadgeClass(a.role))}>
                          {a.role}
                        </span>
                      )}
                      <span className={cn('badge badge-dot capitalize', statusBadgeClass(a.status))}>
                        {a.status}
                      </span>
                    </div>
                    <p className="text-xs text-glass-500 tabular-nums">Assigned {formatAssignedAt(a.assigned_at)}</p>
                    <div className="flex items-center gap-2 flex-wrap text-xs text-glass-400">
                      <span>Assigned by</span>
                      <CreatorProfile creatorId={a.assigned_by} inline />
                    </div>
                  </div>
                  {canLead && a.status === 'active' && (
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => handleComplete(a.id)}
                        disabled={isUpdating}
                        className="btn-icon text-green-400 hover:text-green-300 touch-manipulation"
                        title="Mark completed"
                        aria-label="Mark completed"
                      >
                        {isUpdating ? (
                          <Loader className="w-4 h-4 animate-spin" />
                        ) : (
                          <CheckCircle2 className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemove(a.id)}
                        disabled={removingId === a.id}
                        className="btn-icon text-red-400 hover:text-red-300 touch-manipulation"
                        title="Remove assignment"
                        aria-label="Remove assignment"
                      >
                        {removingId === a.id ? (
                          <Loader className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {confirmAction && (
        <ConfirmationModal
          isOpen
          onClose={() => setConfirmAction(null)}
          onConfirm={executeConfirmAction}
          title={confirmAction.type === 'remove' ? 'Remove Assignment' : 'Complete Assignment'}
          message={confirmAction.type === 'remove'
            ? 'Remove this assignment?'
            : 'Mark this assignment as completed?'}
          confirmText={confirmAction.type === 'remove' ? 'Remove' : 'Complete'}
          isLoading={!!removingId || !!updatingId}
        />
      )}
    </div>
  );
}
